import React, { useCallback, useEffect, useMemo } from 'react';

import {
  decodeDocumentIdFromRelationshipTreeRowId,
  getIsGroupRowIdOrDescendant,
} from '@aline/common';
import type { Theme } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';
import type {
  DataGridPremiumProps,
  GridApi,
  GridApiPremium,
  GridRenderCellParams,
  GridRowParams,
} from '@mui/x-data-grid-premium';
import { gridRowNodeSelector } from '@mui/x-data-grid-premium';
import type { DisplayMode, GroupHeaderRow, ReportGridRow, ReportRow } from 'src/components';
import { RepositoryDocumentNameCell } from 'src/components';
import {
  getIsGroupHeaderRow,
  getIsOrphanRelationshipRow,
  getIsReportGridRow,
  getIsUrlRelationshipRow,
  ROW_CLASS_NOT_CLICKABLE,
} from 'src/components/reports/types';
import type { Apollo } from 'src/gql/generated';
import { getIsBrowserHandledLinkClick } from 'src/helpers';

import { createDataGridPremiumHorizontalScrollStyles } from './data-grid-premium';
import { useReportDataSource } from './reports';
import { useClearSelectionOnRegroup } from './use-clear-selection-on-regroup';
import type { GroupableColumn } from './useGroupableColumns';
import { useGroupingColumns } from './useGroupingColumns';
import { useRepositoryMeta } from './useRepositoryMeta';
import { useDocumentRelationshipSubscription } from './useRepositorySubscriptions';
import { useToggleRowExpansion } from './useToggleRowExpansion';

interface UseDocumentGridOptions {
  apiRef: React.RefObject<GridApi | null>;
  displayMode: DisplayMode;
  missingMatchesOnly: boolean;
  onNavigate: (documentId: string) => void;
  report: Pick<Apollo.Report, 'state'>;
  reportId: UUID;
  unconfirmedRelationshipsOnly: boolean;
  onRowClick?: DataGridPremiumProps['onRowClick'];
  /** Groupable columns the grid defines no column for; see `useGroupingColumns`. */
  virtualGroupableColumns?: readonly GroupableColumn[];
}

interface DocumentGridContextProviderProps {
  displayMode: DisplayMode;
  missingMatchesOnly: boolean;
  reportId: UUID;
  unconfirmedRelationshipsOnly: boolean;
}

export function useDocumentGrid({
  apiRef,
  displayMode,
  missingMatchesOnly,
  onNavigate,
  onRowClick,
  report,
  reportId,
  unconfirmedRelationshipsOnly,
  virtualGroupableColumns,
}: UseDocumentGridOptions) {
  // --- Report data source (server-side rows) ---
  const {
    clearReportDataSourceCaches,
    dataSource,
    dataSourceCache,
    documentCount,
    filterModelTuple: [filterModel, setFilterModel],
    loadedRowCount,
    onDataSourceError,
    removeDocumentsFromDataSource,
    removeGroupFromDataSource,
    rowGroupingModelTuple: [rowGroupingModel, setRowGroupingModel],
    sortModelTuple: [sortModel, setSortModel],
  } = useReportDataSource(reportId, {
    apiRef,
    displayMode,
    missingMatchesOnly,
    report,
    unconfirmedRelationshipsOnly,
  });

  const removeDocumentsFromGrid = useCallback(
    (
      documentIds: readonly UUID[],
      affectedRelationshipParents?: AffectedRelationshipParents | null
    ) => {
      removeDocumentsFromGridApi({
        affectedRelationshipParents,
        api: apiRef.current,
        documentIds,
        removeDocumentsFromDataSource,
      });
    },
    [apiRef, removeDocumentsFromDataSource]
  );

  const removeGroupFromGrid = useCallback(
    (groupRowId: string, trashedCount: number) => {
      removeGroupFromDataSource({ groupRowId, trashedCount });
      removeGroupFromGridApi({ api: apiRef.current, groupRowId });
    },
    [apiRef, removeGroupFromDataSource]
  );

  // --- Selection: a regroup rebuilds every row id, so the old ticks go with it ---
  useClearSelectionOnRegroup({ apiRef, rowGroupingModel });

  // --- Repository meta (relationship pipeline counts) ---
  const {
    detectingRelationshipsCount,
    effectiveMissingMatchesCount,
    failedRelationshipsCount,
    unconfirmedRelationshipsCount,
  } = useRepositoryMeta(reportId);

  // --- Subscriptions ---
  useDocumentRelationshipSubscription();

  // --- Derived "isGrouped" flag (relationship mode always behaves tree-like) ---
  const isGrouped = displayMode !== 'flat' || rowGroupingModel.length > 0;

  /**
   * All modes (flat, grouped, relationship tree data) use MUI 9.4 native lazy
   * loading — no custom lazy-loading hooks.
   */

  // --- Toggle row expansion ---
  const toggleRowExpansion = useToggleRowExpansion(apiRef);

  // --- Render leaf cell ---
  const renderLeafCell = useCallback(
    (cellParams: GridRenderCellParams<ReportRow>) => (
      <RepositoryDocumentNameCell
        {...cellParams}
        value={cellParams.row.__typename === 'Document' ? cellParams.row.name : null}
      />
    ),
    []
  );

  // --- Grouping column definition ---
  const groupingColDef = useGroupingColumns({
    displayMode,
    renderLeafCell,
    rowGroupingModel,
    toggleRowExpansion,
    virtualGroupableColumns,
  });

  // --- Column visibility: hide 'name' when it's already shown in the grouping column ---
  const isNameInGroupingColumn = displayMode === 'relationship' || rowGroupingModel.length > 0;
  useEffect(() => {
    apiRef.current?.setColumnVisibility('name', !isNameInGroupingColumn);
  }, [apiRef, isNameInGroupingColumn]);

  // --- Row class name ---
  const getRowClassName = useCallback(
    (rowParams: GridRowParams) =>
      getIsOrphanRelationshipRow(rowParams.row) || getIsUrlRelationshipRow(rowParams.row)
        ? ROW_CLASS_NOT_CLICKABLE
        : '',
    []
  );

  // --- Row click handler ---
  const handleRowClick = useCallback<NonNullable<DataGridPremiumProps['onRowClick']>>(
    (rowParams, event, details) => {
      /**
       * Skeleton placeholder rows (lazy loading in flight) have no document
       * behind them — clicking one must be a no-op, not a navigation.
       */
      const rowNode = gridRowNodeSelector(apiRef, rowParams.id);
      if (rowNode?.type === 'skeletonRow') return;
      if (getIsOrphanRelationshipRow(rowParams.row)) return;
      if (getIsUrlRelationshipRow(rowParams.row)) return;
      /**
       * Expanding a group is not a navigation, so it still runs for modifier clicks — unlike the
       * document paths below, it has no link the browser could open in a new tab instead.
       */
      if (getIsGroupHeaderRow(rowParams.row)) {
        toggleRowExpansion(rowParams.id);
        return;
      }
      /**
       * A modifier or middle click is a request for a new browser tab. The row background is not a
       * link, so honouring it here would navigate the current tab instead — do nothing and let the
       * row's name link be the thing that opens in a new tab.
       */
      if (getIsBrowserHandledLinkClick(event)) return;

      if (onRowClick) {
        onRowClick(rowParams, event, details);
        return;
      }
      const documentId = decodeDocumentIdFromRelationshipTreeRowId(
        String(rowParams.row?.id ?? rowParams.id)
      );
      onNavigate(documentId);
    },
    [apiRef, onNavigate, onRowClick, toggleRowExpansion]
  );

  // --- Context provider props ---
  const contextProviderProps: DocumentGridContextProviderProps = useMemo(
    () => ({
      displayMode,
      missingMatchesOnly: missingMatchesOnly && displayMode === 'relationship',
      reportId,
      unconfirmedRelationshipsOnly: unconfirmedRelationshipsOnly && displayMode === 'relationship',
    }),
    [displayMode, missingMatchesOnly, reportId, unconfirmedRelationshipsOnly]
  );

  return {
    clearReportDataSourceCaches,
    contextProviderProps,
    dataSource,
    dataSourceCache,
    detectingRelationshipsCount,
    documentCount,
    effectiveMissingMatchesCount,
    failedRelationshipsCount,
    filterModel,
    getRowClassName,
    groupingColDef,
    handleRowClick,
    isGrouped,
    loadedRowCount,
    onDataSourceError,
    removeDocumentsFromGrid,
    removeGroupFromGrid,
    rowGroupingModel,
    setFilterModel,
    setRowGroupingModel,
    setSortModel,
    sortModel,
    unconfirmedRelationshipsCount,
  };
}

type RemoveDocumentsFromDataSource = (documentIds: readonly UUID[]) => void;

/**
 * Surgically removes documents from the grid by ID. Delegates to
 * {@link RemoveDocumentsFromDataSource} (i.e. `removeDocumentsFromDataSource`
 * from `useReportDataSource`) to prune the data-source accumulators +
 * reactive counts, then tells the DataGrid API to drop the matching
 * direct + subtree rows and fix up group headers:
 *
 *  - `updateRows([..., { _action: 'delete' }])` for every direct data row
 *    AND every nested tree-data descendant (id matches `${direct}/...`).
 *  - For grouped mode: decrement each affected `GroupHeaderRow`'s
 *    `childRowCount`; delete the header entirely when its child count hits
 *    zero. Nested intermediate group headers aren't handled here — they're
 *    produced server-side and re-emerge naturally on the next group fetch.
 *  - Clears the selection model (selection no longer maps cleanly).
 *
 * Callers pass document IDs (not row IDs); this function handles the
 * decode from composite tree/group row IDs. Exported so flows that don't
 * use the full {@link useDocumentGrid} hook (e.g. trash view) can reuse
 * the exact same eviction semantics.
 */
export function removeDocumentsFromGridApi({
  affectedRelationshipParents,
  api,
  documentIds,
  removeDocumentsFromDataSource,
}: {
  api: GridApiPremium | null;
  documentIds: readonly UUID[];
  removeDocumentsFromDataSource: RemoveDocumentsFromDataSource;
  affectedRelationshipParents?: AffectedRelationshipParents | null;
}): void {
  if (!api || documentIds.length === 0) return;

  removeDocumentsFromDataSource(documentIds);

  const removedDocSet = new Set(documentIds);
  const allRowIds = [...api.getRowModels().keys()].map(String);
  const directRowIds = allRowIds.filter(rowId =>
    removedDocSet.has(decodeDocumentIdFromRelationshipTreeRowId(rowId))
  );

  const directSet = new Set(directRowIds);
  const subtreeRowIds = allRowIds.filter(
    rowId =>
      !directSet.has(rowId) && directRowIds.some(parentId => rowId.startsWith(`${parentId}/`))
  );
  const deletedSet = new Set([...directRowIds, ...subtreeRowIds]);
  const groupHeaderUpdates = collectGroupHeaderUpdates(api, directRowIds);
  const relationshipParentUpdates = collectRelationshipParentUpdates(
    api,
    affectedRelationshipParents,
    deletedSet
  );
  /**
   * Groups this trash empties. MUI's `updateRows` silently no-ops a group-row
   * delete, leaving a ghost header + a stuck "fetching forever" skeleton, so we
   * remove each emptied group via the prefix-delete MUI *does* honor
   * ({@link removeGroupFromGridApi}) below — the same path whole-group trash uses.
   */
  const emptiedHeaderRows = groupHeaderUpdates.filter(u => u.newCount <= 0).map(u => u.row);
  const emptiedGroupIds = emptiedHeaderRows.map(r => r.id);

  // Trashed leaves inside an emptied group are removed by that group's removal —
  // keep only leaves whose group survives (or flat/relationship rows) here.
  const deletions = [...directRowIds, ...subtreeRowIds]
    .filter(id => !emptiedGroupIds.some(groupId => id === groupId || id.startsWith(`${groupId}/`)))
    .map(id => ({ _action: 'delete' as const, id }));
  const headerDecrements = groupHeaderUpdates
    .filter(u => u.newCount > 0)
    /**
     * `documentCount` is the doc total (header "(N)"); `childRowCount` drives
     * native's `getChildrenCount`/skeletons and is only present on intermediate
     * groups. The subgroup count shouldn't fall below the remaining doc count —
     * `min` keeps it correct without recomputing subgroups, avoiding phantom
     * skeletons after a trash. Leaf groups have no `childRowCount` (it falls
     * back to `documentCount`), so we leave it absent.
     */
    .map(u => ({
      ...u.row,
      documentCount: u.newCount,
      ...(u.row.childRowCount != null
        ? { childRowCount: Math.min(u.row.childRowCount, u.newCount) }
        : {}),
    }));

  /**
   * Relationship-mode document parents. A parent that loses its last child is
   * no longer a Relationship Root, so a root-level (`parentTreeRowId == null`)
   * parent is deleted; a nested parent stays but becomes a leaf (`childRowCount`
   * 0 so `getChildrenCount` reserves no slot — otherwise MUI fills it with the
   * blank skeleton row this fix removes). Otherwise just decrement the count.
   */
  const relationshipParentDeletions = relationshipParentUpdates
    .filter(u => u.newCount <= 0 && u.row.parentTreeRowId == null)
    .map(u => ({ _action: 'delete' as const, id: u.row.id }));
  const relationshipParentDecrements = relationshipParentUpdates
    .filter(u => !(u.newCount <= 0 && u.row.parentTreeRowId == null))
    .map(u => ({ ...u.row, childRowCount: Math.max(0, u.newCount) }));

  /**
   * Count depth-0 removals BEFORE `updateRows` deletes them. `updateRows` is
   * synchronous (default `throttleRowsMs: 0`), so afterward `getRowNode` returns
   * null for every deleted id and the depth filter would always yield 0. Emptied
   * groups are excluded here — `removeGroupFromGridApi` decrements `rowCount` for
   * each of them itself.
   */
  const rootRowsRemoved = [
    ...deletions.map(d => d.id),
    ...relationshipParentDeletions.map(d => d.id),
  ].filter(id => api.getRowNode(id)?.depth === 0).length;

  // Remove emptied groups (header + loaded subtree, + their own rowCount fix)
  // before the survivor updates.
  for (const groupRow of emptiedHeaderRows) {
    removeGroupFromGridApi({ api, groupRowId: groupRow.id });
  }

  api.updateRows([
    ...deletions,
    ...headerDecrements,
    ...relationshipParentDeletions,
    ...relationshipParentDecrements,
  ]);
  /**
   * Synchronously drop MUI's internal lazy-loading rowCount so it doesn't
   * render phantom skeleton rows before React can flush the new `rowCount`
   * prop. Decrement by the number of depth-0 rows actually removed (not the
   * trashed-document count): in flat mode that equals `documentIds.length`,
   * but in grouped/relationship mode one trash can drop a root (an emptied
   * group header, or a now-childless relationship root) while the trashed
   * doc itself was nested — counting depth-0 removals keeps the root skeleton
   * reservation exact in every mode.
   */
  api.setRowCount(Math.max(0, api.state.pagination.rowCount - rootRowsRemoved));
  api.setRowSelectionModel({ ids: new Set(), type: 'include' });
}

/**
 * Surgically removes a whole group from the grid by its row id, for a
 * fully-emptied group trash (every doc in the group trashed, none skipped).
 * Unlike {@link removeDocumentsFromGridApi} this is keyed on the GROUP, not
 * document ids — so it works even when the group is collapsed and its members
 * were never loaded. Cache-independent: no refetch, so it never reads the
 * lagging document-cache.
 *
 * Every descendant of a group with row id `G` has an id prefixed with `G/`
 * (leaf docs are `${parentGroupId}/${docId}`, nested subgroups
 * `${parentGroupId}/${field}:${key}`), so one prefix match selects the entire
 * loaded subtree. The caller restricts this to DEPTH-0 (root) groups, where no
 * ancestor headers need adjusting; a removed root drops the root `rowCount` by
 * one.
 */
export function removeGroupFromGridApi({
  api,
  groupRowId,
}: {
  api: GridApiPremium | null;
  groupRowId: string;
}): void {
  if (!api) return;

  const removedIds = [...api.getRowModels().keys()]
    .map(String)
    .filter(id => getIsGroupRowIdOrDescendant(id, groupRowId));

  // Count depth-0 removals BEFORE updateRows deletes them (see removeDocumentsFromGridApi).
  const rootRowsRemoved = removedIds.filter(id => api.getRowNode(id)?.depth === 0).length;

  api.updateRows(removedIds.map(id => ({ _action: 'delete' as const, id })));
  api.setRowCount(Math.max(0, api.state.pagination.rowCount - rootRowsRemoved));
  api.setRowSelectionModel({ ids: new Set(), type: 'include' });
}

interface GroupHeaderUpdate {
  newCount: number;
  row: GroupHeaderRow;
}

/**
 * Walks the grid's current rows, matches each row slated for deletion to
 * its nearest `GroupHeaderRow` ancestor (via `parentTreeRowId`), and
 * returns per-header childRowCount decrements. Works for any depth of
 * grouping because we look up parents transitively — a doc under
 * `[owner, status]` decrements the `status` header by 1 and the `owner`
 * header by 1.
 */
function collectGroupHeaderUpdates(
  api: GridApiPremium,
  removedRowIds: readonly string[]
): GroupHeaderUpdate[] {
  const rowsLookup = api.getRowModels();
  const decrementByHeaderId = new Map<string, number>();

  for (const rowId of removedRowIds) {
    const row = rowsLookup.get(rowId);
    if (!row) continue;
    if (!('parentTreeRowId' in row)) continue;
    let parentId = row.parentTreeRowId;
    while (typeof parentId === 'string' && parentId.length > 0) {
      const parent = rowsLookup.get(parentId);
      if (!parent || !getIsGroupHeaderRow(parent)) break;
      decrementByHeaderId.set(parentId, (decrementByHeaderId.get(parentId) ?? 0) + 1);
      parentId = parent.parentTreeRowId;
    }
  }

  const updates: GroupHeaderUpdate[] = [];
  for (const [headerId, decrement] of decrementByHeaderId) {
    const header = rowsLookup.get(headerId);
    if (!header || !getIsGroupHeaderRow(header)) continue;
    updates.push({ newCount: header.documentCount - decrement, row: header });
  }
  return updates;
}

export type AffectedRelationshipParents = readonly {
  documentId: UUID;
  trashedChildCount: number;
}[];

interface RelationshipParentUpdate {
  newCount: number;
  row: ReportGridRow;
}

/**
 * Relationship-mode document-parent updates, driven by the server's
 * `affectedRelationshipParents` (which documents lost relationship children to
 * this trash, and how many). The server is authoritative — it knows parents
 * whose affected child wasn't loaded in the grid (a *collapsed* root), which the
 * client can't infer. Each affected parent document may appear in several loaded
 * rows (its own root row plus nested copies under other families); every
 * appearance is updated. The caller deletes a parent that hits zero at the root
 * (no longer a Relationship Root) and converts a nested one to a leaf.
 */
function collectRelationshipParentUpdates(
  api: GridApiPremium,
  affectedRelationshipParents: AffectedRelationshipParents | null | undefined,
  removedSet: ReadonlySet<string>
): RelationshipParentUpdate[] {
  if (!affectedRelationshipParents?.length) return [];
  const trashedByParentDoc = new Map(
    affectedRelationshipParents.map(p => [p.documentId, p.trashedChildCount])
  );

  const updates: RelationshipParentUpdate[] = [];
  for (const [rowId, row] of api.getRowModels()) {
    if (removedSet.has(String(rowId)) || !getIsReportGridRow(row)) continue;
    const trashedChildCount = trashedByParentDoc.get(row.documentId);
    if (trashedChildCount === undefined) continue;
    updates.push({ newCount: (row.childRowCount ?? 0) - trashedChildCount, row });
  }
  return updates;
}

/**
 * Returns a memoized sx style builder for document grid DataGridPremium instances.
 * Handles non-clickable row cursor styling and relationship-mode overlay hiding.
 */
export function useDocumentGridCommonSx(displayMode: DisplayMode, loadedRowCount: number) {
  return useCallback(
    (theme: Theme): SystemStyleObject<Theme> => ({
      ...createDataGridPremiumHorizontalScrollStyles(theme),
      [`& .${ROW_CLASS_NOT_CLICKABLE}`]: {
        cursor: 'default',
      },
      ...(displayMode === 'relationship' && loadedRowCount > 0
        ? {
            '& .MuiDataGrid-overlayWrapper': {
              display: 'none',
            },
          }
        : {}),
    }),
    [displayMode, loadedRowCount]
  );
}
